export const AUTH_RETURN_URL_KEY = 'marineshop.auth.returnUrl';

type RouteStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

export function rememberProtectedRoute(url: URL, storage?: RouteStorage | null): string {
  const path = `${url.pathname}${url.search}${url.hash}`;
  const safePath = safeInternalRedirect(path, '/');

  try {
    storage?.setItem(AUTH_RETURN_URL_KEY, safePath);
  } catch {
    // Storage can be unavailable on locked-down devices; the query redirect still carries the route.
  }

  return safePath;
}

export function buildLoginRedirectUrl(url: URL, storage?: RouteStorage | null): string {
  const path = `${url.pathname}${url.search}${url.hash}`;
  return buildLoginRedirectUrlForPath(path, storage);
}

export function buildLoginRedirectUrlForPath(path: string, storage?: RouteStorage | null): string {
  const returnUrl = safeInternalRedirect(path, '/');

  try {
    storage?.setItem(AUTH_RETURN_URL_KEY, returnUrl);
  } catch {
    // Storage can be unavailable on locked-down devices; the query redirect still carries the route.
  }

  return `/login?redirect=${encodeURIComponent(returnUrl)}`;
}

export function resolveLoginRedirect(
  searchParams: URLSearchParams,
  storage?: RouteStorage | null,
  fallback = '/dashboard'
): string {
  const queryRedirect = safeInternalRedirect(searchParams.get('redirect'), '');
  if (queryRedirect) {
    clearRememberedRoute(storage);
    return queryRedirect;
  }

  let remembered: string | null = null;
  try {
    remembered = storage?.getItem(AUTH_RETURN_URL_KEY) ?? null;
  } catch {
    remembered = null;
  }

  clearRememberedRoute(storage);
  return safeInternalRedirect(remembered, fallback);
}

export function safeInternalRedirect(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim();
  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return fallback;
  }

  try {
    const parsed = new URL(trimmed, 'https://marineshop.local');
    if (parsed.origin !== 'https://marineshop.local') {
      return fallback;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

function clearRememberedRoute(storage?: RouteStorage | null): void {
  try {
    storage?.removeItem(AUTH_RETURN_URL_KEY);
  } catch {
    // Ignore storage errors; redirect safety is handled before this point.
  }
}
